import React, { useState, useEffect, useCallback } from 'react';
import { View, ScrollView, Pressable, ActivityIndicator, StyleSheet } from 'react-native';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';
import { Feather } from '@expo/vector-icons';
import DropDownPicker from 'react-native-dropdown-picker';

import CustomText from '../components/CustomText';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DateInput from '../components/DateInput';
import Util from '../util/util.js';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { t as translate, useTranslation } from '../i18n';

// Labels reuse the shared report-status keys rather than restating them, so a
// status only ever has one translation.
const STATUS_KEYS = ['Open', 'InProgress', 'Closed'];

const getStatusColumns = () =>
  STATUS_KEYS.map((key) => ({ key, label: translate(`reports:status.${key}`) }));

// YYYY-MM-DD → epoch ms (whole-day bounds), same conversion the admin
// report list uses for its server-side date filter.
const dateStringToMillis = (dateStr, endOfDay = false) => {
  if (!dateStr) return null;
  const ms = new Date(dateStr + (endOfDay ? 'T23:59:59.999' : 'T00:00:00')).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * MonetaQueries - Moneta (super-admin) screen for cross-organization queries.
 *
 * First query: report counts per organization broken down by status
 * (Open / In Progress / Closed) and commented vs not commented.
 */
const MonetaQueries = ({ route }) => {
  const { t } = useTranslation('admin');

  const { containerStyle } = useResponsiveStyles();
  const { organization, admin, userAdmin, orgAdmin } = route.params || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);

  // Filters: selected orgs (empty = all) and submission date range
  const [orgFilterOpen, setOrgFilterOpen] = useState(false);
  const [orgFilterValue, setOrgFilterValue] = useState([]);
  const [orgFilterItems, setOrgFilterItems] = useState([]);
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');

  // Org dropdown options load once; query results reload whenever filters change
  useEffect(() => {
    const loadOrgs = async () => {
      try {
        const result = await Util.get('Resty', '/items/organizations/all');
        const orgs = result?.items || [];
        setOrgFilterItems(
          orgs
            .map((org) => org.organization)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ label: name, value: name }))
        );
      } catch (err) {
        console.log('Error loading organizations for filter:', err);
      }
    };
    loadOrgs();
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const minTimestamp = dateStringToMillis(dateRangeStart);
      const maxTimestamp = dateStringToMillis(dateRangeEnd, true);
      const result = await Util.get('Resty', '/items/moneta/queries/report-status-counts', {
        queryParams: {
          ...(orgFilterValue.length > 0 && { organization: orgFilterValue.join(',') }),
          ...(minTimestamp && { minTimestamp }),
          ...(maxTimestamp && { maxTimestamp }),
        },
      });
      if (result?.error) {
        setError(String(result.error));
        return;
      }
      setRows(result?.items || []);
      setTotals(result?.totals || null);
    } catch (err) {
      console.log('Error loading report status counts:', err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [orgFilterValue, dateRangeStart, dateRangeEnd]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const hasActiveFilters = orgFilterValue.length > 0 || dateRangeStart || dateRangeEnd;

  const clearFilters = () => {
    setOrgFilterValue([]);
    setDateRangeStart('');
    setDateRangeEnd('');
  };

  const renderCountCell = (count, keyPrefix) => (
    <View key={keyPrefix} style={styles.countCell}>
      <CustomText font="semiBold" size={14} color={AppStyles.colorSet.textPrimary}>
        {count.total}
      </CustomText>
      <CustomText font="regular" size={11} color={AppStyles.colorSet.textTertiary}>
        {count.commented}💬
      </CustomText>
    </View>
  );

  const renderRow = (row, isTotals = false) => (
    <View
      key={isTotals ? '__totals' : row.organization}
      style={[styles.tableRow, isTotals && styles.totalsRow]}
    >
      <View style={styles.orgCell}>
        <CustomText
          font={isTotals ? 'semiBold' : 'medium'}
          size={14}
          color={AppStyles.colorSet.textPrimary}
        >
          {isTotals ? t('queries.allOrganizations') : row.organization}
        </CustomText>
      </View>
      {getStatusColumns().map((col) =>
        renderCountCell(row.statuses?.[col.key] || { total: 0, commented: 0 }, col.key)
      )}
      <View style={styles.countCell}>
        <CustomText font="semiBold" size={14} color={AppStyles.colorSet.brandPrimary}>
          {row.total}
        </CustomText>
      </View>
      <View style={styles.countCell}>
        <CustomText font="semiBold" size={14} color={AppStyles.colorSet.textPrimary}>
          {row.commented}
        </CustomText>
      </View>
      <View style={styles.countCell}>
        <CustomText font="semiBold" size={14} color={AppStyles.colorSet.textPrimary}>
          {row.total - row.commented}
        </CustomText>
      </View>
    </View>
  );

  return (
    <View style={[containerStyle, styles.container]}>
      <Header organization={organization} admin={admin} userAdmin={userAdmin} />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.pageHeader}>
          <CustomText font="semiBold" size={24} color={AppStyles.colorSet.textPrimary}>
            {t('queries.title')}
          </CustomText>
          <CustomText
            font="regular"
            size={14}
            color={AppStyles.colorSet.textSecondary}
            style={styles.pageSubtitle}
          >
            {t('queries.subtitle')}
          </CustomText>
        </View>

        <View style={styles.section}>
          <View style={styles.queryCard}>
            <View style={styles.queryHeader}>
              <View style={styles.queryHeaderText}>
                <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
                  {t('queries.reportsByOrg')}
                </CustomText>
                <CustomText font="regular" size={13} color={AppStyles.colorSet.textSecondary}>
                  Report counts per organization by status, plus how many have comments (💬)
                  versus none.
                </CustomText>
              </View>
              <Pressable
                onPress={loadCounts}
                disabled={loading}
                style={styles.refreshButton}
                accessibilityRole="button"
                accessibilityLabel={t('queries.refreshLabel')}
              >
                <Feather
                  name="refresh-cw"
                  size={18}
                  color={loading ? AppStyles.colorSet.textTertiary : AppStyles.colorSet.textSecondary}
                />
              </Pressable>
            </View>

            {/* Filters: organization multi-select + submitted date range */}
            <View style={styles.filtersContainer}>
              <View style={styles.orgFilterWrap}>
                <DropDownPicker
                  multiple={true}
                  min={0}
                  open={orgFilterOpen}
                  value={orgFilterValue}
                  items={orgFilterItems}
                  setOpen={setOrgFilterOpen}
                  setValue={setOrgFilterValue}
                  setItems={setOrgFilterItems}
                  placeholder={t('queries.allOrganizations')}
                  multipleText={`${orgFilterValue.length} organization${orgFilterValue.length === 1 ? '' : 's'} selected`}
                  searchable={true}
                  searchPlaceholder="Search organizations..."
                  maxHeight={200}
                  listMode="SCROLLVIEW"
                  zIndex={20}
                />
              </View>
              <View style={styles.dateRangeRow}>
                <Feather name="calendar" size={16} color={AppStyles.colorSet.textSecondary} />
                <CustomText font="regular" size={13} color={AppStyles.colorSet.textSecondary}>
                  {t('queries.submitted')}
                </CustomText>
                <DateInput
                  value={dateRangeStart || ''}
                  onCommit={setDateRangeStart}
                  max={dateRangeEnd}
                  accessibilityLabel={t('queries.startDateLabel')}
                />
                <CustomText font="regular" size={13} color={AppStyles.colorSet.textSecondary}>
                  {t('queries.dateRangeTo')}
                </CustomText>
                <DateInput
                  value={dateRangeEnd || ''}
                  onCommit={setDateRangeEnd}
                  min={dateRangeStart}
                  accessibilityLabel={t('queries.endDateLabel')}
                />
                {hasActiveFilters ? (
                  <Pressable
                    onPress={clearFilters}
                    style={({ hovered, focused, pressed }) => [
                      styles.clearButton,
                      (hovered || focused) && { opacity: 0.8 },
                      pressed && { opacity: 0.6 },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={t('queries.clearLabel')}
                  >
                    <Feather name="x-circle" size={14} color={AppStyles.colorSet.textSecondary} />
                    <CustomText font="regular" size={13} color={AppStyles.colorSet.textSecondary}>
                      {t('queries.clear')}
                    </CustomText>
                  </Pressable>
                ) : null}
              </View>
            </View>

            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={AppStyles.colorSet.brandPrimary} />
              </View>
            ) : error ? (
              <View style={styles.emptyState}>
                <CustomText font="regular" size={14} color={AppStyles.colorSet.error}>
                  {t('queries.loadFailed', { error })}
                </CustomText>
              </View>
            ) : rows.length === 0 ? (
              <View style={styles.emptyState}>
                <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
                  {hasActiveFilters ? t('queries.noMatches') : t('queries.noOrganizations')}
                </CustomText>
              </View>
            ) : (
              <ScrollView horizontal showsHorizontalScrollIndicator style={styles.tableScroll}>
                <View style={styles.table}>
                  <View style={[styles.tableRow, styles.tableHeaderRow]}>
                    <View style={styles.orgCell}>
                      <CustomText font="semiBold" size={12} color={AppStyles.colorSet.textSecondary}>
                        {t('queries.organization')}
                      </CustomText>
                    </View>
                    {getStatusColumns().map((col) => (
                      <View key={col.key} style={styles.countCell}>
                        <CustomText font="semiBold" size={12} color={AppStyles.colorSet.textSecondary}>
                          {col.label}
                        </CustomText>
                      </View>
                    ))}
                    <View style={styles.countCell}>
                      <CustomText font="semiBold" size={12} color={AppStyles.colorSet.textSecondary}>
                        {t('queries.total')}
                      </CustomText>
                    </View>
                    <View style={styles.countCell}>
                      <CustomText font="semiBold" size={12} color={AppStyles.colorSet.textSecondary}>
                        {t('queries.commented')}
                      </CustomText>
                    </View>
                    <View style={styles.countCell}>
                      <CustomText font="semiBold" size={12} color={AppStyles.colorSet.textSecondary}>
                        {t('queries.noComments')}
                      </CustomText>
                    </View>
                  </View>

                  {rows.map((row) => renderRow(row))}
                  {totals && renderRow(totals, true)}
                </View>
              </ScrollView>
            )}
          </View>
        </View>
      </ScrollView>
      <Footer organization={organization} admin={admin} userAdmin={userAdmin} orgAdmin={orgAdmin} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  scrollView: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    paddingBottom: 40,
    flexGrow: 1,
    alignItems: 'stretch',
  },
  pageHeader: {
    paddingHorizontal: 16,
    paddingTop: hp(2),
    paddingBottom: hp(1),
  },
  pageSubtitle: {
    marginTop: 4,
  },
  section: {
    paddingHorizontal: 16,
  },
  queryCard: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    overflow: 'hidden',
  },
  queryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.colorSet.cardBorder,
  },
  queryHeaderText: {
    flex: 1,
    paddingRight: 12,
  },
  refreshButton: {
    padding: 8,
  },
  filtersContainer: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.colorSet.cardBorder,
    zIndex: 20,
  },
  orgFilterWrap: {
    marginBottom: 12,
    zIndex: 20,
  },
  dateRangeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  clearButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  emptyState: {
    paddingVertical: 24,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  tableScroll: {
    width: '100%',
  },
  table: {
    minWidth: '100%',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F1F3',
  },
  tableHeaderRow: {
    backgroundColor: '#F9FAFB',
  },
  totalsRow: {
    backgroundColor: 'rgba(255, 196, 81, 0.08)',
    borderBottomWidth: 0,
  },
  orgCell: {
    width: 180,
    paddingRight: 8,
  },
  countCell: {
    width: 88,
    alignItems: 'center',
  },
});

export default MonetaQueries;
